import styled, { css } from "styled-components";

import {
  controlRadius,
  controlTransition,
  inlineElementHeight,
  inlineElementTypography,
} from "../foundations/controlStyles";
import { PopoverSurface } from "../foundations/popover";
import { yScrollbarStyles } from "../foundations/scrollbar";
import type { InputSize } from "../Input/foundations/inputTypes";

/**
 * `SearchInput`(텍스트 자동완성)과 `TokenSearchInput`(칩 다중선택)이 공유하는 콤보박스 부품.
 * 두 컴포넌트는 selection 모델(자유 텍스트 vs 토큰)만 다르고 입력/메뉴/필터/사이즈는 동일하다.
 */

/** 자동완성 후보. `accentColor`/`description`은 메뉴 옵션의 dot/설명에 쓰인다. */
export type SearchOption = {
  value: string;
  label: string;
  description?: string;
  accentColor?: string;
  disabled?: boolean;
};

export const defaultMaxVisibleOptions = 8;

type ControlSizeStyles = Record<InputSize, ReturnType<typeof css>>;

// 크기 토큰: sm/md 컨트롤의 outer height(box-sizing: border-box)·gap·패딩·폰트 크기.
// 높이는 공용 inlineElementHeight(입력/IconButton/ContentSwitcher 공유)에서 소싱해 dense bar 에서 한 줄
// 높이가 정렬된다. 상하 패딩은 컴팩트하게 — 높이는 min-height 가 잡고 내용은 align-items:center 로 수직 중앙.
export const controlSizeStyles: ControlSizeStyles = {
  sm: css`
    min-height: ${inlineElementHeight.sm};
    gap: 0.26rem;
    padding: 0.08rem 0.34rem;
    font-size: ${inlineElementTypography.sm.fontSize};
    line-height: ${inlineElementTypography.sm.lineHeight};
  `,
  md: css`
    min-height: ${inlineElementHeight.md};
    gap: 0.34rem;
    padding: 0.12rem 0.42rem;
    font-size: ${inlineElementTypography.md.fontSize};
    line-height: ${inlineElementTypography.md.lineHeight};
  `,
};

export const Shell = styled.div<{ $width?: number | string }>`
  position: relative;
  display: flex;
  width: ${({ $width }) => toCssLength($width) ?? "100%"};
  min-width: 0;
  align-items: center;
  gap: 0.5rem;
`;

export const Control = styled.div<{
  $inputSize: InputSize;
  $invalid: boolean;
  $disabled: boolean;
}>`
  position: relative;
  box-sizing: border-box;
  display: flex;
  width: 100%;
  min-width: 0;
  flex: 1 1 auto;
  flex-wrap: wrap;
  align-items: center;
  /* 입력류 rest 보더는 subtle — ContentSwitcher 트랙과 같은 단으로 톤다운(가장자리가
     형제 컨트롤보다 튄다는 리뷰). 3:1 대비는 hover(strong)/focus에서 확보. */
  border: 1px solid ${({ theme }) => theme.color.border.subtle};
  ${controlRadius}
  background: transparent;
  color: ${({ theme }) => theme.color.text.default};
  ${controlTransition("border-color", "background-color")}

  ${({ $inputSize }) => controlSizeStyles[$inputSize]}

  &:hover {
    border-color: ${({ theme }) => theme.color.border.strong};
  }

  /* 컨테이너 border류 — focus는 outline 링 없이 border-color만 교체(Input family와 동일). */
  &:has(:focus-visible) {
    border-color: ${({ theme }) => theme.color.border.focus};
  }

  ${({ $invalid, theme }) =>
    $invalid &&
    css`
      border-color: ${theme.color.border.danger};
      &:hover,
      &:has(:focus-visible) {
        border-color: ${theme.color.border.danger};
      }
      /* invalid+focus만 border-color로 구분 불가라 얇은 1px outline 추가. */
      &:has(:focus-visible) {
        outline: 1px solid ${theme.color.border.focus};
        outline-offset: 1px;
      }
    `}

  ${({ $disabled, theme }) =>
    $disabled &&
    css`
      border-color: ${theme.color.border.disabled};
      background: ${theme.color.bg.surfaceSunken};
      color: ${theme.color.text.disabled};
      cursor: not-allowed;
      pointer-events: none;
      user-select: none;
    `}
`;

export const Input = styled.input`
  flex: 1 1 9rem;
  width: 100%;
  min-width: 5rem;
  border: 0;
  outline: none;
  background: transparent;
  color: inherit;
  font: inherit;

  &::placeholder {
    color: ${({ theme }) => theme.color.text.subtle};
  }

  &:disabled {
    color: ${({ theme }) => theme.color.text.disabled};
    cursor: not-allowed;
    -webkit-text-fill-color: ${({ theme }) => theme.color.text.disabled};
  }

  &[type="search"]::-webkit-search-decoration,
  &[type="search"]::-webkit-search-cancel-button,
  &[type="search"]::-webkit-search-results-button,
  &[type="search"]::-webkit-search-results-decoration {
    appearance: none;
  }
`;

// 컨트롤(bordered box) 안쪽 오른쪽 끝의 액세서리 영역. 외부 accessory + 내부 clear 버튼을 담는다.
// margin-left:auto 로 토큰이 줄바꿈해도 마지막 줄의 오른쪽 끝에 붙는다.
export const Accessories = styled.div`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.25rem;
  margin-left: auto;
  align-self: center;
`;

/** 입력 안쪽 오른쪽 끝에 두는 액세서리(예: 단위 텍스트 "개"/"원"). 소비처가 `accessory` prop에 넣는다. */
export const SearchInputAccessory = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  color: ${({ theme }) => theme.color.text.subtle};
  font: inherit;
  line-height: 1;
  white-space: nowrap;
  user-select: none;
`;

// 메뉴는 컨트롤(bordered box)에 앵커된다. left:-1px + width:calc(100%+2px)로 메뉴 외곽 테두리를
// 컨트롤 외곽 테두리와 정확히 일치시킨다(컨트롤 1px border만큼 안쪽으로 들어가던 1px inset 제거).
// 이 폭 계산은 border-box 전제 — c6는 전역 box-sizing 리셋이 없어(content-box 기본) 여기서 명시하지
// 않으면 padding(0.3rem)+border가 폭 바깥에 더해져 메뉴가 오른쪽으로 ~스크롤바 폭만큼 삐져나온다.
export const Menu = styled(PopoverSurface)`
  box-sizing: border-box;
  top: calc(100% + 0.35rem);
  left: -1px;
  width: calc(100% + 2px);
  max-height: 20rem;
  ${yScrollbarStyles}
  padding: 0.3rem;
  border: 1px solid ${({ theme }) => theme.color.border.subtle};
  border-radius: ${({ theme }) => theme.shape.radius.surface};
  background: ${({ theme }) => theme.color.bg.surface};
  box-shadow: 0 0.75rem 1.5rem
    color-mix(in srgb, ${({ theme }) => theme.color.scrim.default} 14%, transparent);
`;

export const Option = styled.button<{ $active: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  padding: 0.48rem 0.55rem;
  border: 0;
  ${controlRadius}
  background: ${({ $active, theme }) => ($active ? theme.color.bg.surfaceRaised : "transparent")};
  color: ${({ theme }) => theme.color.text.default};
  cursor: pointer;
  font: inherit;
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.color.bg.surfaceRaised};
  }

  &:disabled {
    color: ${({ theme }) => theme.color.text.disabled};
    cursor: not-allowed;
  }
`;

export const OptionMain = styled.span`
  display: grid;
  min-width: 0;
  gap: 0.25rem;
`;

export const OptionLabel = styled.span<{ $accentColor?: string }>`
  min-width: 0;
  overflow: hidden;
  color: inherit;
  font-weight: ${({ theme }) => theme.typography.weight.extrabold};
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ $accentColor }) =>
    $accentColor &&
    css`
      &::before {
        display: inline-block;
        width: 0.55rem;
        height: 0.55rem;
        margin-right: 0.42rem;
        border-radius: 0.14rem;
        background: ${$accentColor};
        content: "";
        vertical-align: -0.03rem;
      }
    `}
`;

export const OptionDescription = styled.span`
  overflow: hidden;
  color: ${({ theme }) => theme.color.text.subtle};
  font-size: ${({ theme }) => theme.typography.size.caption};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const EmptyText = styled.span`
  display: block;
  padding: 0.6rem 0.55rem;
  color: ${({ theme }) => theme.color.text.subtle};
  font-size: ${({ theme }) => theme.typography.size.caption};
`;

// width prop(number=px, string=CSS 길이)을 CSS 값으로. 미지정이면 undefined → Shell이 100% fallback.
export function toCssLength(value: number | string | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  return typeof value === "number" ? `${value}px` : value;
}

export function filterOptions({
  options,
  excludeValues,
  query,
  maxVisibleOptions,
  getOptionKeywords,
}: {
  options: readonly SearchOption[];
  excludeValues: ReadonlySet<string>;
  query: string;
  maxVisibleOptions: number;
  getOptionKeywords?: (option: SearchOption) => readonly string[];
}): SearchOption[] {
  const normalized = normalizeQuery(query);
  const filtered: SearchOption[] = [];

  for (const option of options) {
    if (excludeValues.has(option.value)) {
      continue;
    }
    if (normalized !== "" && !matchesOption(option, normalized, getOptionKeywords)) {
      continue;
    }
    filtered.push(option);
    if (filtered.length >= maxVisibleOptions) {
      break;
    }
  }

  return filtered;
}

function matchesOption(
  option: SearchOption,
  query: string,
  getOptionKeywords?: (option: SearchOption) => readonly string[],
): boolean {
  const keywords = getOptionKeywords?.(option) ?? [
    option.label,
    option.value,
    option.description ?? "",
  ];
  return keywords.some((keyword) => normalizeQuery(keyword).includes(query));
}

export function moveActiveIndex(
  currentIndex: number,
  options: readonly SearchOption[],
  delta: 1 | -1,
): number {
  if (options.length === 0) {
    return -1;
  }

  let nextIndex = currentIndex;
  for (let step = 0; step < options.length; step += 1) {
    nextIndex = (nextIndex + delta + options.length) % options.length;
    if (!options[nextIndex].disabled) {
      return nextIndex;
    }
  }

  return -1;
}

export function normalizeQuery(value: string): string {
  return value.trim().toLowerCase();
}

export const EMPTY_VALUE_SET: ReadonlySet<string> = new Set();
